import { getColaMedicaWaitMs, pickColaTierByWaitMs } from './pedidoColaMedica';

const CHAT_NO_REPLY_MS = 10 * 60 * 1000;

const PEDIDO_PROGRESS = new Set([
  'pendiente_derivacion',
  'pendiente_aprobacion_medica',
  'pendiente',
  'pendiente_call_center',
  'aprobado',
  'en_tratamiento',
  'pedido_generado_farmacia',
  'en_preparacion',
  'en_camino',
  'confirmada_dispensa'
]);

const PEDIDO_DEMORADO_CHECK = new Set([
  'pendiente_derivacion',
  'pendiente_aprobacion_medica',
  'pendiente_call_center',
  'pendiente',
  'en_preparacion',
  'en_camino'
]);

const PEDIDO_LABELS = {
  pendiente_derivacion: { label: 'En curso', tone: 'progress' },
  pendiente_aprobacion_medica: { label: 'En curso', tone: 'progress' },
  pendiente: { label: 'Pendiente emisión', tone: 'pending' },
  pendiente_call_center: { label: 'En curso', tone: 'progress' },
  aprobado: { label: 'Aprobado', tone: 'active' },
  rechazado: { label: 'Rechazado', tone: 'expired' },
  en_tratamiento: { label: 'En curso', tone: 'progress' },
  finalizado: { label: 'Finalizado', tone: 'muted' },
  emitida_sistema_externo: { label: 'Vigente', tone: 'active' },
  confirmada_dispensa: { label: 'Con dispensa', tone: 'progress' },
  entregada: { label: 'Entregada', tone: 'muted' },
  pedido_generado_farmacia: { label: 'En curso', tone: 'progress' },
  en_preparacion: { label: 'En curso', tone: 'progress' },
  en_camino: { label: 'En curso', tone: 'progress' },
  entregado: { label: 'Entregado', tone: 'active' },
  cancelado: { label: 'Cancelado', tone: 'expired' }
};

const RECETA_LABELS = {
  pendiente: { label: 'Pendiente', tone: 'pending' },
  emitida_sistema_externo: { label: 'Vigente', tone: 'active' },
  confirmada_dispensa: { label: 'Vigente', tone: 'active' },
  entregada: { label: 'Entregada', tone: 'muted' }
};

function parseDateMs(value) {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  const ms = date.getTime();
  return Number.isFinite(ms) ? ms : null;
}

function withDefaults(badge) {
  if (!badge) return null;
  return {
    showDot: false,
    showShield: false,
    ...badge
  };
}

export function resolveTycStatusBadge(tyc) {
  if (!tyc || tyc.aceptado === false) {
    return withDefaults({ label: 'Pendiente', tone: 'pending', showDot: true });
  }
  const vigenteHastaMs = parseDateMs(tyc.vigente_hasta);
  if (vigenteHastaMs != null && vigenteHastaMs < Date.now()) {
    return withDefaults({ label: 'Vencido', tone: 'expired', showDot: true });
  }
  return withDefaults({ label: 'Al día', tone: 'active', showShield: true });
}

export function resolveRecetaStatusBadge(recetaOrEstado) {
  const receta =
    recetaOrEstado && typeof recetaOrEstado === 'object' ? recetaOrEstado : { estado: recetaOrEstado };
  const estado = String(receta?.estado || '').trim();
  const vencimientoMs = parseDateMs(
    receta?.fechavencimiento_plex || receta?.fecha_vencimiento || receta?.vigente_hasta
  );
  if (vencimientoMs != null && vencimientoMs < Date.now() && estado !== 'pendiente') {
    return withDefaults({ label: 'Vencida', tone: 'expired', showDot: true });
  }
  const mapped = RECETA_LABELS[estado];
  if (mapped) {
    return withDefaults({
      ...mapped,
      showShield: mapped.tone === 'active',
      showDot: mapped.tone === 'pending'
    });
  }
  const fallback = estado.replaceAll ? estado.replaceAll('_', ' ') : estado || 'Sin estado';
  return withDefaults({ label: fallback, tone: 'muted' });
}

export function resolvePedidoStatusBadge(pedidoOrEstado, nowMs = Date.now()) {
  const pedido =
    pedidoOrEstado && typeof pedidoOrEstado === 'object' ? pedidoOrEstado : { estado: pedidoOrEstado };
  const estado = String(pedido?.estado || '').trim();
  if (!estado) return withDefaults({ label: 'Sin estado', tone: 'muted' });

  if (PEDIDO_DEMORADO_CHECK.has(estado)) {
    const waitMs = getColaMedicaWaitMs(pedido, nowMs);
    const tier = pickColaTierByWaitMs(waitMs);
    if (tier.id === 'warning' || tier.id === 'critical') {
      return withDefaults({ label: 'Demorado', tone: 'warning', showDot: true });
    }
    if (PEDIDO_PROGRESS.has(estado)) {
      return withDefaults({ label: 'En curso', tone: 'progress', showDot: true });
    }
  }

  const mapped = PEDIDO_LABELS[estado];
  if (mapped) {
    return withDefaults({
      ...mapped,
      showShield: mapped.tone === 'active',
      showDot: mapped.tone === 'progress' || mapped.tone === 'pending'
    });
  }

  return withDefaults({
    label: estado.replaceAll('_', ' '),
    tone: 'muted'
  });
}

export function resolveClienteStatusBadge(cliente) {
  if (!cliente) return null;
  const dni = String(cliente.dni_normalizado || '')
    .replace(/\D/g, '')
    .replace(/^0+/, '');
  const hasName = Boolean(
    String(cliente.apellido_geclisa || cliente.nombre_geclisa || cliente.nombre_plex || '').trim()
  );
  const hasPhone = [cliente.telefono_plex, cliente.telefono_geclisa, cliente.celular_geclisa].some((v) =>
    String(v || '').trim()
  );
  const hasOrigin =
    cliente.codcliente != null ||
    cliente.ficha_id != null ||
    (cliente.fecha_alta_sanatorio != null && String(cliente.fecha_alta_sanatorio).trim());

  const incomplete = !dni || dni.length <= 6 || !hasName || !hasPhone || !hasOrigin;
  if (incomplete) {
    return withDefaults({ label: 'Datos incompletos', tone: 'warning', showDot: true });
  }
  return withDefaults({ label: 'Al día', tone: 'active', showShield: true });
}

export function resolveRecetaCruzadaStatusBadge(status) {
  const key = String(status || '').trim();
  if (key === 'Cruce con Plex') {
    return withDefaults({ label: 'Validada', tone: 'active', showShield: true });
  }
  if (key === 'Validada sin cruce') {
    return withDefaults({ label: 'Sin cruce', tone: 'pending', showDot: true });
  }
  return withDefaults({ label: 'Sin validar', tone: 'pending', showDot: true });
}

export function resolveChatStatusBadge(conversation, nowMs = Date.now()) {
  if (!conversation) return null;
  const unread = Number(conversation.unread_count || 0) > 0;
  if (unread) {
    return withDefaults({ label: 'Sin leer', tone: 'warning', showDot: true });
  }

  const status = String(conversation.status || '').trim().toLowerCase();
  if (status === 'resolved') {
    return withDefaults({ label: 'Resuelto', tone: 'muted' });
  }

  const last = conversation.last_message;
  const lastAt = parseDateMs(last?.created_at);
  const incoming =
    String(last?.message_type || '').toLowerCase() === 'incoming' ||
    String(last?.sender_type || '').toLowerCase() === 'contact';
  if (status === 'open' && incoming && lastAt != null && nowMs - lastAt >= CHAT_NO_REPLY_MS) {
    return withDefaults({ label: 'Sin respuesta', tone: 'warning', showDot: true });
  }

  if (status === 'pending') {
    return withDefaults({ label: 'Pendiente', tone: 'pending', showDot: true });
  }

  return withDefaults({ label: 'Al día', tone: 'active', showShield: true });
}
